import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  CharterAttachmentBody,
  CharterAttachmentPayload,
  CharterLedger,
  CharterStatsPayload,
  CharterSummary,
  ConfirmCharterAttachmentUploadRequest,
  CreateCharterRequest,
  PrepareCharterAttachmentUploadRequest,
  SearchChartersRequest,
  UpdateCharterLedgerPayload,
  UpdateCharterRequest,
} from '@ankor-io/common/types/charter'
import type {
  CreateTransactionBody,
  ListTransactionsRequest,
  TransactionPayload,
} from '@ankor-io/common/types/transaction/transaction'

import { charterService } from '@/api/services/charter.service'
import { useUserContext } from '@/providers/user/useUserContext'

import { invalidateBoatCashFloatQueries } from './cashFloatInvalidation'
import { validateCharterAttachmentQueryParams, validateCharterQueryParams, validateContextIds } from './helpers'

/**
 * TanStack Query hook for searching charters.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const { data: charters, isLoading, error } = useSearchChartersQuery({ take: 10, skip: 0 })
 * ```
 */
export function useSearchChartersQuery(params?: SearchChartersRequest & { enabled?: boolean }) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { enabled = true, ...searchParams } = params || {}

  const isEnabled =
    enabled && Boolean(activeOrganizationId) && Boolean(activeVesselId) && Boolean(activeConnectedAccountId)

  return useQuery<CharterSummary[]>({
    queryKey: ['charters', activeOrganizationId, activeVesselId, activeConnectedAccountId, searchParams],
    queryFn: () => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return charterService.searchCharters(orgId, vesselId, connectedAccountId, searchParams)
    },
    enabled: isEnabled,
    placeholderData: (previousData) => previousData,
  })
}

/**
 * TanStack Query hook for fetching a single charter by ID.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const { data: charter, isLoading, error } = useGetCharterByIdQuery({ charterId: 'charter_123' })
 * ```
 */
export function useGetCharterByIdQuery(params: { charterId: string; enabled?: boolean }) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { charterId, enabled = true } = params

  const isEnabled =
    enabled &&
    Boolean(activeOrganizationId) &&
    Boolean(activeVesselId) &&
    Boolean(activeConnectedAccountId) &&
    Boolean(charterId)

  return useQuery<CharterSummary>({
    queryKey: ['charter', activeOrganizationId, activeVesselId, activeConnectedAccountId, charterId],
    queryFn: () => {
      const {
        orgId,
        vesselId,
        connectedAccountId,
        charterId: validatedCharterId,
      } = validateCharterQueryParams(activeOrganizationId, activeVesselId, activeConnectedAccountId, charterId)

      return charterService.getCharterById(orgId, vesselId, connectedAccountId, validatedCharterId)
    },
    enabled: isEnabled,
  })
}

/**
 * TanStack Query hook for fetching charter statistics.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const { data: stats, isLoading, error } = useCharterStatsQuery({ charterId: 'charter_123' })
 * ```
 */
export function useCharterStatsQuery(params: { charterId: string; enabled?: boolean }) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { charterId, enabled = true } = params

  const isEnabled =
    enabled &&
    Boolean(activeOrganizationId) &&
    Boolean(activeVesselId) &&
    Boolean(activeConnectedAccountId) &&
    Boolean(charterId)

  return useQuery<CharterStatsPayload>({
    queryKey: ['charter-stats', activeOrganizationId, activeVesselId, activeConnectedAccountId, charterId],
    queryFn: () => {
      const {
        orgId,
        vesselId,
        connectedAccountId,
        charterId: validatedCharterId,
      } = validateCharterQueryParams(activeOrganizationId, activeVesselId, activeConnectedAccountId, charterId)

      return charterService.getCharterStats(orgId, vesselId, connectedAccountId, validatedCharterId)
    },
    enabled: isEnabled,
  })
}

/**
 * TanStack Query hook for fetching charter transactions.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const { data: transactions, isLoading, error } = useCharterTransactionsQuery({ charterId: 'charter_123', take: 10, skip: 0 })
 * ```
 */
export function useCharterTransactionsQuery(
  params: { charterId: string } & ListTransactionsRequest & { enabled?: boolean },
) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { charterId, enabled = true, ...listParams } = params

  const isEnabled =
    enabled &&
    Boolean(activeOrganizationId) &&
    Boolean(activeVesselId) &&
    Boolean(activeConnectedAccountId) &&
    Boolean(charterId)

  return useQuery<TransactionPayload[]>({
    queryKey: [
      'charter-transactions',
      activeOrganizationId,
      activeVesselId,
      activeConnectedAccountId,
      charterId,
      listParams,
    ],
    queryFn: () => {
      const {
        orgId,
        vesselId,
        connectedAccountId,
        charterId: validatedCharterId,
      } = validateCharterQueryParams(activeOrganizationId, activeVesselId, activeConnectedAccountId, charterId)

      return charterService.listCharterTransactions(orgId, vesselId, connectedAccountId, validatedCharterId, listParams)
    },
    enabled: isEnabled,
  })
}

/**
 * TanStack Query hook for fetching charter digital sign-off ledger.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const { data: ledger, isLoading, error } = useCharterDigitalSignOffLedgerQuery({ charterId: 'charter_123' })
 * ```
 */
export function useCharterDigitalSignOffLedgerQuery(params: { charterId: string; enabled?: boolean }) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { charterId, enabled = true } = params

  const isEnabled =
    enabled &&
    Boolean(activeOrganizationId) &&
    Boolean(activeVesselId) &&
    Boolean(activeConnectedAccountId) &&
    Boolean(charterId)

  return useQuery<{ ledger: CharterLedger }>({
    queryKey: [
      'charter-digital-signoff-ledger',
      activeOrganizationId,
      activeVesselId,
      activeConnectedAccountId,
      charterId,
    ],
    queryFn: () => {
      const {
        orgId,
        vesselId,
        connectedAccountId,
        charterId: validatedCharterId,
      } = validateCharterQueryParams(activeOrganizationId, activeVesselId, activeConnectedAccountId, charterId)

      return charterService.getCharterDigitalSignOffLedger(orgId, vesselId, connectedAccountId, validatedCharterId)
    },
    enabled: isEnabled,
  })
}

/**
 * TanStack Query hook for fetching charter attachments.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const { data: attachments, isLoading, error } = useCharterAttachmentsQuery({ charterId: 'charter_123', take: 10, skip: 0 })
 * ```
 */
export function useCharterAttachmentsQuery(params: {
  charterId: string
  take?: number
  skip?: number
  enabled?: boolean
}) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { charterId, enabled = true, ...listParams } = params

  const isEnabled =
    enabled &&
    Boolean(activeOrganizationId) &&
    Boolean(activeVesselId) &&
    Boolean(activeConnectedAccountId) &&
    Boolean(charterId)

  return useQuery<CharterAttachmentPayload[]>({
    queryKey: [
      'charter-attachments',
      activeOrganizationId,
      activeVesselId,
      activeConnectedAccountId,
      charterId,
      listParams,
    ],
    queryFn: () => {
      const {
        orgId,
        vesselId,
        connectedAccountId,
        charterId: validatedCharterId,
      } = validateCharterQueryParams(activeOrganizationId, activeVesselId, activeConnectedAccountId, charterId)

      return charterService.listCharterAttachments(orgId, vesselId, connectedAccountId, validatedCharterId, listParams)
    },
    enabled: isEnabled,
  })
}

/**
 * TanStack Query hook for fetching a charter attachment by ID.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const { data: attachment, isLoading, error } = useGetCharterAttachmentByIdQuery({
 *   charterId: 'charter_123',
 *   attachmentId: 'att_456'
 * })
 * ```
 */
export function useGetCharterAttachmentByIdQuery(params: {
  charterId: string
  attachmentId: string
  enabled?: boolean
}) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { charterId, attachmentId, enabled = true } = params

  const isEnabled =
    enabled &&
    Boolean(activeOrganizationId) &&
    Boolean(activeVesselId) &&
    Boolean(activeConnectedAccountId) &&
    Boolean(charterId) &&
    Boolean(attachmentId)

  return useQuery<CharterAttachmentPayload>({
    queryKey: [
      'charter-attachment',
      activeOrganizationId,
      activeVesselId,
      activeConnectedAccountId,
      charterId,
      attachmentId,
    ],
    queryFn: () => {
      const {
        orgId,
        vesselId,
        connectedAccountId,
        charterId: validatedCharterId,
        attachmentId: validatedAttachmentId,
      } = validateCharterAttachmentQueryParams(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
        charterId,
        attachmentId,
      )

      return charterService.getCharterAttachmentById(
        orgId,
        vesselId,
        connectedAccountId,
        validatedCharterId,
        validatedAttachmentId,
      )
    },
    enabled: isEnabled,
  })
}

// ========== Mutation Hooks ==========

/**
 * TanStack Query mutation hook for creating a new charter.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const createCharter = useCreateCharterMutation()
 * createCharter.mutate({
 *   name: 'Summer Charter',
 *   startDate: '2024-06-01',
 *   endDate: '2024-06-15',
 *   // ... other fields
 * })
 * ```
 */
export function useCreateCharterMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async (payload: CreateCharterRequest) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return charterService.createCharter(orgId, vesselId, connectedAccountId, payload)
    },
    onSuccess: () => {
      // Invalidate charter list queries to refetch with the new charter
      queryClient.invalidateQueries({
        queryKey: ['charters', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })
    },
  })
}

/**
 * TanStack Query mutation hook for updating a charter.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const updateCharter = useUpdateCharterMutation()
 * updateCharter.mutate({
 *   charterId: 'charter_123',
 *   payload: { name: 'Updated Charter', status: CharterStatus.IN_PROGRESS, ... }
 * })
 * ```
 */
export function useUpdateCharterMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({ charterId, payload }: { charterId: string; payload: UpdateCharterRequest }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return charterService.updateCharter(orgId, vesselId, connectedAccountId, charterId, payload)
    },
    onSuccess: (_data, variables) => {
      // Invalidate charter list queries
      queryClient.invalidateQueries({
        queryKey: ['charters', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate specific charter query
      queryClient.invalidateQueries({
        queryKey: ['charter', activeOrganizationId, activeVesselId, activeConnectedAccountId, variables.charterId],
      })

      // Invalidate charter stats query
      queryClient.invalidateQueries({
        queryKey: [
          'charter-stats',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.charterId,
        ],
      })
    },
  })
}

/**
 * TanStack Query mutation hook for creating a transaction for a charter.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const createTransaction = useCreateCharterTransactionMutation()
 * createTransaction.mutate({
 *   charterId: 'charter_123',
 *   payload: { originalAmountCents: 10000, originalAmountCurrencyCode: 'USD', ... }
 * })
 * ```
 */
export function useCreateCharterTransactionMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({ charterId, payload }: { charterId: string; payload: CreateTransactionBody }) => {
      const {
        orgId,
        vesselId,
        connectedAccountId,
        charterId: validatedCharterId,
      } = validateCharterQueryParams(activeOrganizationId, activeVesselId, activeConnectedAccountId, charterId)
      return charterService.createCharterTransaction(orgId, vesselId, connectedAccountId, validatedCharterId, payload)
    },
    onSuccess: (_data, variables) => {
      // Invalidate charter transactions list
      queryClient.invalidateQueries({
        queryKey: [
          'charter-transactions',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.charterId,
        ],
      })

      // Invalidate transaction list queries
      queryClient.invalidateQueries({
        queryKey: ['transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate my transactions list queries
      queryClient.invalidateQueries({
        queryKey: ['my-transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate my transactions infinite list queries (used by spender role)
      queryClient.invalidateQueries({
        queryKey: ['my-transactions-infinite', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate charter stats query
      queryClient.invalidateQueries({
        queryKey: [
          'charter-stats',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.charterId,
        ],
      })

      invalidateBoatCashFloatQueries({
        queryClient,
        organizationId: activeOrganizationId,
        vesselId: activeVesselId,
        paymentMethod: variables.payload.paymentMethod,
        cashFloatId: variables.payload.cashFloatId,
      })
    },
  })
}

/**
 * TanStack Query mutation hook for updating charter digital sign-off ledger.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const updateLedger = useUpdateCharterDigitalSignOffLedgerMutation()
 * updateLedger.mutate({
 *   charterId: 'charter_123',
 *   payload: { signOffType: SignOffType.DIGITAL, clientSignature: '...', ... }
 * })
 * ```
 */
export function useUpdateCharterDigitalSignOffLedgerMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({ charterId, payload }: { charterId: string; payload: UpdateCharterLedgerPayload }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return charterService.updateCharterDigitalSignOffLedger(orgId, vesselId, connectedAccountId, charterId, payload)
    },
    onSuccess: (data, variables) => {
      // Invalidate charter queries
      queryClient.invalidateQueries({
        queryKey: ['charter', activeOrganizationId, activeVesselId, activeConnectedAccountId, variables.charterId],
      })

      // Invalidate charter digital sign-off ledger query
      queryClient.invalidateQueries({
        queryKey: [
          'charter-digital-signoff-ledger',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.charterId,
        ],
      })

      // Invalidate charter list queries
      queryClient.invalidateQueries({
        queryKey: ['charters', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate charter transactions list (e.g. gratuity transaction create/update)
      queryClient.invalidateQueries({
        queryKey: [
          'charter-transactions',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.charterId,
        ],
      })

      // Invalidate transaction list queries
      queryClient.invalidateQueries({
        queryKey: ['transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate my transactions list queries
      queryClient.invalidateQueries({
        queryKey: ['my-transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate charter stats query
      queryClient.invalidateQueries({
        queryKey: [
          'charter-stats',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.charterId,
        ],
      })

      // Invalidate the gratuity transaction detail so View Gratuity shows the updated amount
      if (data.gratuityTransactionId) {
        queryClient.invalidateQueries({
          queryKey: [
            'transaction',
            activeOrganizationId,
            activeVesselId,
            activeConnectedAccountId,
            data.gratuityTransactionId,
          ],
        })
      }
    },
  })
}

/**
 * TanStack Query mutation hook for creating a charter attachment.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const createAttachment = useCreateCharterAttachmentMutation()
 * createAttachment.mutate({
 *   charterId: 'charter_123',
 *   payload: { name: 'receipt.pdf', mimeType: 'application/pdf' }
 * })
 * ```
 */
export function useCreateCharterAttachmentMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({ charterId, payload }: { charterId: string; payload: CharterAttachmentBody }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return charterService.createCharterAttachment(orgId, vesselId, connectedAccountId, charterId, payload)
    },
    onSuccess: (_data, variables) => {
      // Invalidate charter attachments list
      queryClient.invalidateQueries({
        queryKey: [
          'charter-attachments',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.charterId,
        ],
      })
    },
  })
}

/**
 * TanStack Query mutation hook for preparing charter attachment upload.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const prepareUpload = usePrepareCharterAttachmentUploadMutation()
 * prepareUpload.mutate({
 *   charterId: 'charter_123',
 *   payload: { name: 'receipt.pdf', mimeType: 'application/pdf', charterId: 'charter_123', uploadedBy: 'user_123' }
 * })
 * ```
 */
export function usePrepareCharterAttachmentUploadMutation() {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({
      charterId,
      payload,
    }: {
      charterId: string
      payload: PrepareCharterAttachmentUploadRequest
    }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return charterService.prepareCharterAttachmentUpload(orgId, vesselId, connectedAccountId, charterId, payload)
    },
  })
}

/**
 * TanStack Query mutation hook for confirming charter attachment upload.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const confirmUpload = useConfirmCharterAttachmentUploadMutation()
 * confirmUpload.mutate({
 *   charterId: 'charter_123',
 *   payload: { uploadToken: 'token_123' }
 * })
 * ```
 */
export function useConfirmCharterAttachmentUploadMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({
      charterId,
      payload,
    }: {
      charterId: string
      payload: ConfirmCharterAttachmentUploadRequest
    }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return charterService.confirmCharterAttachmentUpload(orgId, vesselId, connectedAccountId, charterId, payload)
    },
    onSuccess: (_data, variables) => {
      // Invalidate charter attachments list
      queryClient.invalidateQueries({
        queryKey: [
          'charter-attachments',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.charterId,
        ],
      })
    },
  })
}

/**
 * TanStack Query mutation hook for deleting a charter attachment.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const deleteAttachment = useDeleteCharterAttachmentMutation()
 * deleteAttachment.mutate({
 *   charterId: 'charter_123',
 *   attachmentId: 'att_456'
 * })
 * ```
 */
export function useDeleteCharterAttachmentMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({ charterId, attachmentId }: { charterId: string; attachmentId: string }) => {
      const {
        orgId,
        vesselId,
        connectedAccountId,
        charterId: validatedCharterId,
        attachmentId: validatedAttachmentId,
      } = validateCharterAttachmentQueryParams(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
        charterId,
        attachmentId,
      )

      return charterService.deleteCharterAttachment(
        orgId,
        vesselId,
        connectedAccountId,
        validatedCharterId,
        validatedAttachmentId,
      )
    },
    onSuccess: (_data, variables) => {
      // Invalidate charter attachments list
      queryClient.invalidateQueries({
        queryKey: [
          'charter-attachments',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.charterId,
        ],
      })

      // Invalidate specific attachment query
      queryClient.invalidateQueries({
        queryKey: [
          'charter-attachment',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.charterId,
          variables.attachmentId,
        ],
      })
    },
  })
}
